import React, { useCallback, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineWalletIcon from 'LuxKit/Icons/line/LineWalletIcon'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

interface Props {
  activityBudget: Array<{
    activityBudget: string
    selected: boolean
  }>
  onBudgetSelect: (budget: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripBudgetSelection({
  activityBudget,
  onBudgetSelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((budget: string) => {
    onBudgetSelect(budget)
    onNext()
  }, [onBudgetSelect, onNext])

  const tripBudgetMap = useMemo(() =>
    arrayToMap(activityBudget, budget => budget.activityBudget, budget => budget.selected),
  [activityBudget])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'activity_budget',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const TRIP_BUDGETS = [
    {
      activityBudget: 'A Little Splash',
      title: intl.formatMessage({
        id: 'y8H0q63AH/',
        defaultMessage: 'A Little Splash',
        description: 'Title for budget-conscious activity budget option',
      }),
      description: intl.formatMessage({
        id: 'gnm6nVAGNp',
        defaultMessage: 'Save where I can, spend where it counts.',
        description: 'Description for budget-conscious activity budget option',
      }),
      icon: <LineUsdCircleIcon />,
    },
    {
      activityBudget: 'Mix & Match',
      title: intl.formatMessage({
        id: 'vm+UW0n68A',
        defaultMessage: 'Mix & Match',
        description: 'Title for moderate activity budget option',
      }),
      description: intl.formatMessage({
        id: 'DDge0fRKvP',
        defaultMessage: 'Some paid fun, every other day is fine.',
        description: 'Description for moderate activity budget option',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      activityBudget: 'Open Wallet',
      title: intl.formatMessage({
        id: 'nxzh9UB6By',
        defaultMessage: 'Open Wallet',
        description: 'Title for generous activity budget option',
      }),
      description: intl.formatMessage({
        id: 'PdjCPWzDwr',
        defaultMessage: 'If it sounds amazing, I’m in.',
        description: 'Description for generous activity budget option',
      }),
      icon: <LineWalletIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: '8JEddBvSRL',
      defaultMessage: 'Activity <i>Budget</i>',
      description: 'Heading text with italicized Budget',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'vCGD0NitKQ',
    defaultMessage: 'What’s your activity budget looking like? (Don’t worry — we’ll get to food shortly)',
    description: 'Question asking about activity budget preference',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>
      <TileCardsGroup>
        {TRIP_BUDGETS.map((budget) => (
          <TileCard
            key={budget.activityBudget}
            title={budget.title}
            subtitle={budget.description}
            icon={budget.icon}
            value={budget.activityBudget}
            onClick={handleOnClick}
            selected={!!tripBudgetMap.get(budget.activityBudget)}
            disabled={isGeneratingTrip}
          />
        ))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripBudgetSelection
